import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { Skull, Crosshair, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PenTestSuite = () => {
  const [exploits, setExploits] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/pentest/exploits").then(({ data }) => setExploits(data));
  }, []);

  const filtered = exploits.filter(
    (e) => filter === "all" || e.rating === filter
  );

  const run = async (e) => {
    setRunning(true);
    setActive({ exploit: e.name, cve: e.cve, trace: ["[*] Initializing..."], outcome: null });
    try {
      const { data } = await api.post(`/pentest/run/${e.id}`);
      // animate trace
      for (let i = 0; i <= data.trace.length; i++) {
        await new Promise((r) => setTimeout(r, 250));
        setActive({ ...data, trace: data.trace.slice(0, i) });
      }
      setActive(data);
      toast.success(`${e.name} executed: ${data.outcome.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Attack Surface / Pen Test"
        title="Pen Test Suite"
        accent="pink"
        subtitle="Curated exploit catalog with autonomous execution and attack-chain telemetry."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            {["all", "weaponized", "stable"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={[
                  "text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-all",
                  filter === f ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300" : "border-slate-800 text-slate-400 hover:border-slate-700"
                ].join(" ")}
              >
                {f}
              </button>
            ))}
            <span className="ml-auto text-xs text-slate-500">{filtered.length} modules</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((e) => (
              <div key={e.id} data-testid={`exploit-${e.cve}`} className="csx-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-fuchsia-300 truncate">{e.name}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{e.cve}</div>
                  </div>
                  <span className={[
                    "text-[9px] px-1.5 py-0.5 uppercase tracking-widest border",
                    e.rating === "weaponized" ? "border-rose-500/60 text-rose-300 bg-rose-500/10" : "border-amber-500/60 text-amber-300 bg-amber-500/10"
                  ].join(" ")}>
                    {e.rating}
                  </span>
                </div>
                <div className="text-xs text-slate-300 mt-2">{e.category}</div>
                <div className="text-[10px] text-slate-500 mt-1">{e.platform}</div>
                <div className="mt-3 flex justify-end">
                  <TButton variant="accent" icon={running && active?.cve === e.cve ? Loader2 : Crosshair}
                    onClick={() => run(e)} disabled={running}>
                    {running && active?.cve === e.cve ? "Running" : "Execute"}
                  </TButton>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="csx-card p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-3">
            <Skull className="h-3 w-3" />
            Live Exploit Trace
          </div>
          {!active ? (
            <div className="text-xs text-slate-500">Select an exploit to view live trace.</div>
          ) : (
            <div className="font-mono text-[12px] space-y-1">
              <div className="text-fuchsia-300 font-bold">{active.exploit}</div>
              <div className="text-slate-400">{active.cve}</div>
              <div className="border-t border-slate-800 my-2" />
              {active.trace.map((t, i) => (
                <div key={`${i}-${t}`} className={t.startsWith("[!]") ? "text-rose-400" : t.startsWith("[+]") ? "text-emerald-400" : t.startsWith("[*]") ? "text-amber-300" : "text-slate-300"}>
                  {t}
                </div>
              ))}
              {active.outcome && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400">Outcome</span>
                  <div className={[
                    "mt-1 text-sm font-bold uppercase tracking-widest",
                    active.outcome === "session_opened" ? "text-emerald-400" :
                    active.outcome === "payload_blocked" ? "text-rose-400" : "text-amber-300"
                  ].join(" ")}>{active.outcome.replace(/_/g, " ")}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PenTestSuite;
